import { InfoTip } from "@/components/ui/info-tip";
import type { FormEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Camera, LogOut, Mail, ShieldCheck, Users } from "lucide-react";
import { readCsrfToken } from "@/lib/api";
import { AvatarPicker } from "@/components/AvatarPicker";
import { ContactPreferences } from "@/components/ContactPreferences";
import { ManagerAccessPanel } from "@/components/ManagerAccessPanel";
import { NotificationPreferencesPanel } from "@/components/NotificationPreferencesPanel";
import { SavedRepliesPanel } from "@/components/SavedRepliesPanel";
import { useIsAgent, useSession } from "@/components/session-provider";
import { TimeZonePicker } from "@/components/TimeZonePicker";
import { ThemeToggle } from "@/components/ThemeToggle";
import { QueueLayoutControl } from "@/components/tickets/QueueLayoutControl";
import { defaultQueueLayoutFor, useQueueLayout } from "@/components/tickets/queue-layout";

export const Route = createFileRoute("/profile")({
  component: ProfilePage,
});

/**
 * Puts the CURRENT antiforgery token into the form, at the moment of submission.
 *
 * ⚠️ Read at submit time, never at render time, and that distinction is the whole bug.
 *
 * The server reissues the token cookie on every safe request, so a value captured when
 * the component rendered is frozen in the DOM while the real one moves on. React renders
 * this page once; the token then rotates underneath it — and it stops matching outright
 * if the signed-in principal's claims change, because the token is bound to the user, not
 * just to the cookie. By the time the button is clicked the captured value is stale and
 * the POST is rejected as an invalid token.
 *
 * This is precisely why `api()` never had the problem: it reads the cookie on every call.
 * Doing the same here makes the form obey the same rule as every other write in the app.
 *
 * If there is no token the form still submits and the server refuses it. That is the
 * correct outcome — a navigation carries no header to fall back to, so there is nothing
 * to recover with, and failing closed is the rule.
 */
function attachCsrfToken(event: FormEvent<HTMLFormElement>) {
  const field = event.currentTarget.elements.namedItem(
    "__RequestVerificationToken",
  ) as HTMLInputElement | null;

  if (field) field.value = readCsrfToken() ?? "";
}

/**
 * The signed-in person's own account.
 *
 * Everything shown is the caller's own record. There is deliberately no way to look up
 * anyone else from here — a profile screen that accepts a user id is a directory, and a
 * directory of ~29 sites' staff is not something a requester needs.
 */
function ProfilePage() {
  const { user } = useSession();
  const isAgent = useIsAgent();

  // The ticket list's layout (D-153). The same control the list's own toolbar draws, reading
  // the same stored preference — a layout, kept on this device, like the theme beside it.
  const { layout, setLayout } = useQueueLayout(defaultQueueLayoutFor(isAgent));

  if (!user) return null;

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6 sm:py-10">
      <header className="min-w-0">
        <h1 className="truncate text-xl font-semibold tracking-tight">{user.displayName}</h1>
        {user.email && (
          <p className="flex items-center gap-1.5 truncate text-sm text-muted-foreground">
            <Mail className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {user.email}
          </p>
        )}
      </header>

      <section className="group/card mt-6 rounded-2xl border border-border bg-card p-4">
        <h2 className="flex items-center gap-2 text-sm font-medium">
          <Camera className="h-4 w-4 text-muted-foreground" aria-hidden />
          Profile picture
          <InfoTip id="profile-picture" className="-ml-1" />
        </h2>

        <div className="mt-3">
          <AvatarPicker userId={user.userId} name={user.displayName} />
        </div>
      </section>

      {/* Above access rather than below it: this is the part of the screen a person came
          here to change, and access is something they read once. */}
      <ContactPreferences />

      {/* ⚠️ Directly under "how we reach you", because it is the other half of the same
          question: those panels say where messages go, this one says which ones are sent.
          Reading them in the other order leaves somebody setting a phone number for
          notifications they have just turned off.

          On the PROFILE and not in Settings, for D-94's reason: Settings is fenced to
          `settings.manage`, which is three people, and this is a setting about oneself that
          every employee at ~29 sites needs. It renders nothing if the server has the feature
          switched off. */}
      <NotificationPreferencesPanel />

      <section className="group/card mt-6 rounded-2xl border border-border bg-card p-4">
        <h2 className="flex items-center gap-2 text-sm font-medium">
          <ShieldCheck className="h-4 w-4 text-muted-foreground" aria-hidden />
          Your access
          <InfoTip id="profile-access" className="-ml-1" />
        </h2>

        <div className="mt-3 space-y-3 text-sm">
          <div>
            <p className="text-xs text-muted-foreground">Roles</p>
            <p className="mt-0.5">{user.roles.length > 0 ? user.roles.join(", ") : "None yet"}</p>
          </div>

          <div>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Users className="h-3 w-3" aria-hidden /> Teams
            </p>
            <p className="mt-0.5">
              {user.teams.length > 0 ? user.teams.join(", ") : "Not a member of any team"}
            </p>
          </div>
        </div>

        {/* Said plainly. People ask why they cannot see something, and "ask an
            administrator" is a more useful answer than a screen full of empty panels. */}
        <p className="mt-4 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
          Access is granted inside this application by an administrator. It is never taken from your
          Entra groups, so joining a group elsewhere will not change what you see here.
        </p>
      </section>

      {/* Directly beneath "Your access", because it is the other half of the same subject —
          that panel is what this person may reach, this one is what they let somebody else
          reach. The only grant on the screen they control themselves. */}
      <ManagerAccessPanel />

      {/* ⚠️ On the PROFILE rather than in Settings, and that placement is the decision (D-94).
          Settings is fenced to `settings.manage` — three people in the organisation — which is
          exactly why this feature was unusable by the agents it exists for. A saved reply is
          somebody's own working material, so it belongs beside their timezone and their avatar.

          ⚠️ It draws nothing at all for a requester (D-136) — not an empty panel, which is what
          it used to draw. The gate is `canUseCannedResponses` from `/api/me`, computed from the
          expression the endpoints check, because the browser's own permission list carries no
          scope and every employee holds `ticket.comment` at `Own`. */}
      <SavedRepliesPanel />

      <section className="group/card mt-4 rounded-2xl border border-border bg-card p-4">
        <h2 className="flex items-center gap-1 text-sm font-medium">
          Appearance
          <InfoTip id="profile-appearance" />
        </h2>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Theme</span>
          <ThemeToggle />
        </div>

        <div className="mt-3 flex items-center justify-between gap-4">
          <div>
            <span className="text-sm text-muted-foreground">Ticket list</span>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Split reads the selected request beside the list on a wide screen. Remembered on this
              device.
            </p>
          </div>
          <QueueLayoutControl value={layout} onChange={setLayout} />
        </div>

        <TimeZonePicker />
      </section>

      {/*
        A REAL form POST — no fetch, no preventDefault.

        ⚠️ It used to fetch and then set window.location itself, and that broke sign-out
        twice over. Signing out redirects to Entra to end the session there, back to the
        app, and on to the configured landing page; fetch consumes that chain internally
        so the browser never moves, and the hard-coded "/" then sent the user back to the
        app to be signed straight in again by SSO.

        Only a navigation follows redirects. A navigation cannot set a header, so the
        antiforgery token travels as a hidden field instead — same value, same check.

        POST rather than GET because a GET sign-out is a one-click denial of service from
        any page that can embed an image tag.
      */}
      <form method="post" action="/api/auth/logout" className="mt-4" onSubmit={attachCsrfToken}>
        <input type="hidden" name="__RequestVerificationToken" defaultValue="" />
        <button
          type="submit"
          className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-input text-sm transition hover:bg-accent sm:h-10 sm:w-auto sm:px-4"
        >
          <LogOut className="h-4 w-4" aria-hidden /> Sign out
        </button>
      </form>
    </div>
  );
}
